import React, { useEffect, useState } from "react";
import { Form, Input, Button, Spin, Typography } from "antd";
import {
  UserOutlined,
  ArrowLeftOutlined,
  CloseCircleFilled,
  CheckCircleFilled,
  LoadingOutlined,
} from "@ant-design/icons";
import { TResponseModel } from "../../types/config";
import { useLocation, useNavigate } from "react-router-dom";
import useFetch from "../../hooks/useFetch";
import { eFONTFAMILY, eLoginMode, eResultCode } from "../../utils/enum";
import useToast from "../../hooks/useToast";
import { ToastOpen, ToastType } from "../../state/toast/slice";
import styles from "./auth.module.css";
import EncryptUtils from "../../utils/encrypt";
import {
  ApiSignin,
  ChangePasswordOpen,
  GenerateOtpOpen,
  GetMenuHierarchyList,
  ValidateOTPOpen,
} from "../../utils/api.constant";
import AuthUtil from "../../utils/auth";
import Utils from "../../utils";

const ForgotPassword: React.FC = () => {
  // URL QUERY PARAMS SETUP
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const emailId = atob(queryParams.get("email") || "");
  const loginFormEmail = atob(queryParams.get("e") || "");
  const autoTrigger = queryParams.get("at");

  const navigate = useNavigate();
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [autoLogin, setAutoLogin] = useState<boolean>(false);
  const [isAutoProcessing, setIsAutoProcessing] = useState<boolean>(
    autoTrigger === "t",
  );
  const [isOTPSent, setIsOTPSent] = useState<boolean>(false);
  const [otpToken, setOTPToken] = useState<string>("");
  const [isResendMode, setIsResendMode] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(300);
  const [targetTime, setTargetTime] = useState<number>(0);
  const [otp, setOtp] = useState<string>("");
  const [isOTPVerified, setIsOTPVerified] = useState<boolean>(false);
  const [form] = Form.useForm();
  const { Text } = Typography;

  const email = Form.useWatch("email", form);

  useEffect(() => {
    if (autoTrigger === "t") {
      if (loginFormEmail) {
        form.setFieldValue("email", loginFormEmail);
        (async () => {
          await onSendOTP({ email: loginFormEmail }, true);
          setIsAutoProcessing(false);
        })();
      } else {
        setIsAutoProcessing(false);
      }
    }
  }, []);

  const onSendOTP = async (
    values: { email: string },
    isAutoTrigger: boolean = false,
  ) => {
    try {
      setIsLoading(true);
      const payload = {
        data: {
          emailId: values.email,
        },
      };
      const response: TResponseModel = await post(GenerateOtpOpen, payload);

      const { dataResponse, data } = response;
      const { returnCode, description } = dataResponse;

      if (returnCode === eResultCode.SUCCESS) {
        setOTPToken(data?.otpToken);
        setIsOTPSent(true);
        setIsResendMode(false);
        setTimer(300);
        setTargetTime(Date.now() + 300 * 1000);
        if (!isAutoTrigger) {
          onShowToast({
            type: ToastType.success,
            title: <CheckCircleFilled />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: description,
        });
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onVerifyOTP = async (values: { otp: string }) => {
    try {
      setIsLoading(true);
      const email = form.getFieldValue("email");
      const payload = {
        data: {
          emailId: email,
          otp: values.otp,
          otpToken,
        },
      };

      const response: TResponseModel = await post(ValidateOTPOpen, payload);

      const { dataResponse, data } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        if (values.otp === EncryptUtils.decrypt(data?.otpToken).split(":")[1]) {
          onShowToast({
            type: ToastType.success,
            title: <CheckCircleFilled />,
            position: ToastOpen.leftBottom,
            content: description,
          });
          if (autoTrigger === "t") {
            await loginWithOTPToken(email, data?.otpToken);
          } else {
            setIsOTPVerified(true);
          }
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseCircleFilled />,
            position: ToastOpen.leftBottom,
            content: "Error validating OTP, please try again!",
          });
        }
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: description,
        });
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch menu hierarchy list from the server
  const getMenuHierarchyList = async () => {
    try {
      const requestPayload = {
        data: {
          renderMenuRoleWise: true,
        },
      };
      const response = await post(GetMenuHierarchyList, requestPayload);
      const { data, dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        return data;
      } else {
        console.error(`Error while fetching menu hierarchy: ${description}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const loginWithOTPToken = async (email: string, otpToken: string) => {
    // need to call the sign in API so that user can login automatically once the OTP gets verify
    try {
      setAutoLogin(true);

      // Prepare payload for API request
      const payload = {
        userName: email,
        password: otpToken,
        loginMode: eLoginMode.OTP,
      };

      // Make API request for sign-in
      const response: TResponseModel = await post(ApiSignin, payload);

      // Handle API response
      const { dataResponse, authToken } = response;
      const { returnCode, description } = dataResponse;

      if (returnCode === eResultCode.SUCCESS) {
        AuthUtil.setToken(authToken as string);
        AuthUtil.activeLoggedUserService();
        setAutoLogin(false);
        // Redirect to dashboard after a brief delay
        setTimeout(async () => {
          const menus = await getMenuHierarchyList();
          Utils.redirectUrl(menus?.[0]?.entityUrl);
        }, 700);
      } else {
        // Display error toast
        setAutoLogin(false);
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setAutoLogin(false);
    }
  };

  const onSubmitNewPassword = async (values: {
    email: string;
    password: string;
    confirmPassword: string;
  }) => {
    if (values.password !== values.confirmPassword) {
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Passwords do not match!",
      });
      return;
    }

    try {
      setIsLoading(true);
      const email = form.getFieldValue("email");
      const encryptedEmail = EncryptUtils.encrypt(email); // Encrypt email to base64
      const password = EncryptUtils.encrypt(values.password as string);
      const encryptedOTP = EncryptUtils.encrypt(otp as string);
      const payload = {
        data: {
          emailId: encryptedEmail,
          password: password,
          otp: encryptedOTP,
        },
      };
      const response: TResponseModel = await post(ChangePasswordOpen, payload);

      const { dataResponse } = response;
      const { returnCode, description } = dataResponse;

      if (returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: description,
        });
        navigate("/login"); // Redirect to login after password reset
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: description,
        });
      }
    } catch (error) {
      console.error("Error submitting new password:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Timer countdown
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (targetTime > 0) {
      interval = setInterval(() => {
        const secondsRemaining = Math.ceil((targetTime - Date.now()) / 1000);

        if (secondsRemaining <= 0) {
          setTimer(0);
          setIsResendMode(true);
          setOtp("");
          form.resetFields(["otp"]);
          setTargetTime(0);
          clearInterval(interval);
        } else {
          setTimer(secondsRemaining);
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [targetTime, form]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const backToLogin = () => {
    navigate(`/login?e=${email ? btoa(email) : ""}`);
  };

  return (
    <div className={styles.authWrapper}>
      <div className={styles.parentWrapper}>
        {!isOTPVerified ? (
          !isOTPSent ? (
            isAutoProcessing ? (
              <div
                className={styles.container}
                style={{
                  height: "300px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 52 }} spin />}
                />
              </div>
            ) : (
              <Form
                form={form}
                name="forgotPassword"
                onFinish={onSendOTP}
                size="middle"
                initialValues={{ email: emailId || loginFormEmail }}
                autoComplete="off"
              >
                <div className={styles.formHeader}>
                  <span
                    style={{
                      fontFamily: eFONTFAMILY.SEMIBOLD,
                      fontSize: "22px",
                    }}
                  >
                    Change Password?
                  </span>
                </div>
                <p>Please enter your email to receive an OTP</p>
                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: "Please input your Email!" },
                    { type: "email", message: "Please enter a valid Email!" },
                  ]}
                >
                  <Input
                    disabled={emailId ? true : false}
                    size="large"
                    autoComplete="off"
                    prefix={<UserOutlined />}
                    placeholder="Email"
                  />
                </Form.Item>
                <Form.Item>
                  <Button
                    block
                    type="primary"
                    htmlType="submit"
                    loading={isLoading}
                    disabled={isLoading}
                    size="large"
                  >
                    Send OTP
                  </Button>
                </Form.Item>
              </Form>
            )
          ) : (
            <Form name="verifyOTP" onFinish={onVerifyOTP} size="middle">
              <div className={styles.formHeader}>
                <h1>Verify OTP</h1>
              </div>
              <p>Enter the OTP sent to your email address</p>
              <Form.Item
                name="otp"
                rules={[{ required: true, message: "Please input the OTP!" }]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="OTP"
                  value={otp}
                  autoComplete="off"
                  onChange={(e) => setOtp(e.target.value)}
                />
                {timer === 0 && (
                  <p style={{ color: "red", fontSize: "12px" }}>
                    *OTP has expired. Please resend a new OTP.
                  </p>
                )}
              </Form.Item>
              <Form.Item>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <p className={styles.timer} style={{ marginTop: "5px" }}>
                    Remaining time :{" "}
                    <span style={{ fontWeight: "600" }}>
                      {formatTime(timer)}
                    </span>
                  </p>
                  <Button
                    type="link"
                    loading={isLoading && isResendMode}
                    disabled={!isResendMode}
                    onClick={() =>
                      onSendOTP({ email: form.getFieldValue("email") })
                    }
                  >
                    Resend OTP?
                  </Button>
                </div>
              </Form.Item>
              <Form.Item>
                <Button
                  block
                  disabled={isLoading || timer == 0}
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                >
                  Verify OTP
                </Button>
              </Form.Item>
            </Form>
          )
        ) : (
          <Form name="newPassword" onFinish={onSubmitNewPassword} size="middle">
            <div className={styles.formHeader}>
              <h1>Set New Password</h1>
            </div>
            <Form.Item
              name="password"
              rules={[
                { required: true, message: "Please input your new password!" },
                {
                  pattern:
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/,
                  message:
                    "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character!",
                },
              ]}
            >
              <Input.Password autoComplete="off" placeholder="New Password" />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              rules={[
                {
                  required: true,
                  message: "Please confirm your new password!",
                },
                {
                  pattern:
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/,
                  message:
                    "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character!",
                },
              ]}
            >
              <Input.Password
                autoComplete="off"
                placeholder="Confirm New Password"
              />
            </Form.Item>
            <Form.Item>
              <Button
                block
                type="primary"
                htmlType="submit"
                loading={isLoading}
              >
                Submit
              </Button>
            </Form.Item>
          </Form>
        )}
        {autoLogin ? (
          <div className={styles.container}>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 36 }} spin />}
            />
            <Text className={styles.text}>Auto login in progress...</Text>
          </div>
        ) : (
          !isAutoProcessing && (
            <Button type="link" onClick={backToLogin}>
              <ArrowLeftOutlined />
              Back
            </Button>
          )
        )}
        {/* <Button type="link" onClick={backToLogin}>
          <ArrowLeftOutlined />
          Back
        </Button> */}
      </div>
    </div>
  );
};

export default ForgotPassword;
